import graphqlFetch from '@helpers/api/graphql-fetch';
import { gql } from 'graphql-request';

import { generateUrlWithQueryParameter, REMOVE_FALSY } from '@helpers/url';
import { clientLogger } from '@services/logger/clientLogger';
import {
  TAllArticlesBLog,
  TArticlesBlogDataProps,
  TArticlesData,
  TSearchArticlesProps,
} from 'types/blog/blog.types';
import { responsiveImage } from '../helpers/imageGeneric';

const VALID_STATUS = [200];

export const articleBlogDataGql = (filter: string): string => gql`
  blogArticle(${filter}) {
    seo {
      description
      title
    }
    title
    abstract
    slug
    author {
      name
    }
    content(markdown: true)
    image{
      ${responsiveImage({ params: { auto: 'format', w: 800 } })}
    }
    date
    relatedArticles {
      slug
      title
    }
    category {
      name
      slug
    }
    createdAt
    updatedAt
  }
`;

export const allBlogArticlesData = (filter: string): string => gql`
  allBlogArticles(${filter}) {
    abstract
    title
    slug
    readTime
    category {
      name
      id
      slug
    }
    author{
      name
      photo {
        url
      }
    }
    image {
      ${responsiveImage({ params: { auto: 'format', w: 600 } })}
    }
  }
`;

const blogArticlesData = (type: string): string => {
  const variables = {
    all: {
      params: '$id: [ItemId], $first: IntType!, $skip: IntType!',
      filter: `filter: { category: { anyIn: $id } , active: {eq: true}, blogType: {eq: "default" } }, first: $first, skip: $skip, orderBy: [date_DESC]`,
      query: allBlogArticlesData,
      extraQuery: `_allBlogArticlesMeta(filter: { category: { anyIn: $id }, active: {eq: true}, blogType: {eq: "default" }  }) { count }`,
    },
    single: {
      params: '$slug: String',
      filter: `filter: { slug: { eq: $slug }, active: { eq: true }, blogType: {eq: "default" } }`,
      query: articleBlogDataGql,
      extraQuery: '',
    },
    search: {
      params: '$title: String!, $abstract: String!, $first: IntType!, $skip: IntType!',
      filter: `filter: { OR: [{ title: { matches: { pattern: $title } } },{ abstract: { matches: { pattern: $abstract } } },], AND: {blogType: {eq: "default" }}  }, first: $first, skip: $skip, orderBy: [date_DESC]`,
      query: allBlogArticlesData,
      extraQuery:
        '_allBlogArticlesMeta(filter: {OR: [{ title: { matches: { pattern: $title } } },{ abstract: { matches: { pattern: $abstract } } },]}) { count }',
    },
  };

  const { params, filter, query, extraQuery } = variables[type];

  return gql`
    query blogArticle(${params}) {
      ${query(filter)}
      ${extraQuery}
    }
  `;
};

export const getSearchArticlesData = async ({
  search,
  preview = false,
  displayItemCount,
  page,
}: TSearchArticlesProps): Promise<TAllArticlesBLog> =>
  graphqlFetch(blogArticlesData('search'), {
    preview,
    variables: {
      title: search,
      abstract: search,
      first: displayItemCount,
      skip: (page - 1) * displayItemCount,
    },
  });

export async function getArticleData({
  preview,
  slug = '',
  page = 1,
  displayItemCount = 10,
  type,
  categoryId = '',
}: TArticlesData): Promise<any> {
  const variables: { first: number; skip: number; id?: string[]; slug?: string } = {
    first: displayItemCount,
    skip: (page - 1) * displayItemCount,
  };

  if (categoryId) {
    variables.id = [categoryId];
  }

  if (slug) {
    variables.slug = slug;
  }

  const { blogArticle, allBlogArticles, _allBlogArticlesMeta } = await graphqlFetch(
    blogArticlesData(type),
    {
      preview,
      variables,
    }
  );

  return {
    blogArticle: blogArticle || null,
    allBlogArticles: allBlogArticles || null,
    _allBlogArticlesMeta: _allBlogArticlesMeta || null,
  };
}

export async function getServerlessBlogAllArticlesData({
  page = 1,
  displayItemCount = 11,
  categoryId = '',
}: TArticlesBlogDataProps): Promise<any> {
  try {
    const domain = typeof window === 'undefined' ? process.env.NEXT_PUBLIC_HTTP_DOMAIN : '';

    const url = generateUrlWithQueryParameter(
      `${domain}/api/datocms/common-data/blog/all-articles`,
      {
        page,
        displayItemCount,
        categoryId,
      },
      REMOVE_FALSY
    );

    const response = await fetch(url);

    if (!VALID_STATUS.includes(response.status)) {
      throw new Error(`Invalid response status ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    clientLogger.serviceRequestFailed('Failed to fetch blog all articles data from DatoCMS proxy', {
      service: 'datocms',
      action: 'get_serverless_blog_all_articles_data',
      context: { reason: e?.message, stack: e?.stack, page, displayItemCount, categoryId },
    });
    return null;
  }
}
